
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { X, Plus, Thermometer, Heart, Activity, AlertCircle, CheckCircle, Info } from "lucide-react";
import { TriageData } from "@/types/triage";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SymptomIntakeProps {
  onComplete: (data: Partial<TriageData>) => void;
  initialData: TriageData;
}

const commonSymptoms = [
  "Fever", "Cough", "Headache", "Nausea", "Vomiting", "Diarrhea",
  "Chest pain", "Shortness of breath", "Abdominal pain", "Dizziness",
  "Fatigue", "Muscle aches", "Sore throat", "Runny nose", "Rash"
];

const severityLevels = [
  { value: "mild", label: "Mild - Barely noticeable", color: "bg-green-100 text-green-800" },
  { value: "moderate", label: "Moderate - Noticeable but manageable", color: "bg-yellow-100 text-yellow-800" },
  { value: "severe", label: "Severe - Significantly affects daily activities", color: "bg-orange-100 text-orange-800" },
  { value: "unbearable", label: "Unbearable - Cannot function normally", color: "bg-red-100 text-red-800" }
];

export function SymptomIntake({ onComplete, initialData }: SymptomIntakeProps) {
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>(initialData.symptoms);
  const [customSymptom, setCustomSymptom] = useState("");
  const [severity, setSeverity] = useState(initialData.severity);
  const [duration, setDuration] = useState(initialData.duration);
  const [onset, setOnset] = useState(initialData.onset);
  const [additionalInfo, setAdditionalInfo] = useState(initialData.additionalInfo);
  const [vitals, setVitals] = useState(initialData.vitals);
  
  // Form validation states
  const [touched, setTouched] = useState({
    symptoms: false,
    severity: false,
    duration: false,
    onset: false
  });
  const [formProgress, setFormProgress] = useState(0);

  const addSymptom = (symptom: string) => {
    if (!selectedSymptoms.includes(symptom)) {
      setSelectedSymptoms([...selectedSymptoms, symptom]);
    }
  };

  const removeSymptom = (symptom: string) => {
    setSelectedSymptoms(selectedSymptoms.filter(s => s !== symptom));
  };

  const addCustomSymptom = () => {
    if (customSymptom.trim() && !selectedSymptoms.includes(customSymptom.trim())) {
      setSelectedSymptoms([...selectedSymptoms, customSymptom.trim()]);
      setCustomSymptom("");
    }
  };

  // Calculate form completion percentage
  useEffect(() => {
    let completed = 0;
    const total = 4; // Total required fields/sections
    
    if (selectedSymptoms.length > 0) completed++;
    if (severity) completed++;
    if (duration) completed++;
    if (onset) completed++;
    
    setFormProgress(Math.round((completed / total) * 100));
  }, [selectedSymptoms, severity, duration, onset]);
  
  const handleSubmit = () => {
    // Mark all fields as touched for validation
    setTouched({
      symptoms: true,
      severity: true,
      duration: true,
      onset: true
    });
    
    if (isComplete) {
      onComplete({
        symptoms: selectedSymptoms,
        severity,
        duration,
        onset,
        additionalInfo,
        vitals
      });
    }
  };

  const isComplete = selectedSymptoms.length > 0 && severity && duration && onset;
  
  // Helper function to get validation state
  const getValidationState = (field: keyof typeof touched, value: any) => {
    if (!touched[field]) return "untouched";
    return value ? "valid" : "invalid";
  };

  return (
    <div className="space-y-6">
      {/* Form Completion Progress */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium">Form completion</span>
          <span className="text-sm font-medium">{formProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${formProgress}%` }}
          ></div>
        </div>
      </div>
      {/* Symptom Selection */}
      <CollapsibleCard
        title={<div className="flex items-center gap-2">
          <span>What symptoms are you experiencing?</span>
          {getValidationState("symptoms", selectedSymptoms.length > 0) === "valid" && (
            <CheckCircle className="w-5 h-5 text-green-500" />
          )}
          {getValidationState("symptoms", selectedSymptoms.length > 0) === "invalid" && (
            <AlertCircle className="w-5 h-5 text-red-500" />
          )}
        </div>}
        description="Select all that apply or add your own"
        defaultOpen={true}
      >
        <div className="space-y-4">
          {touched.symptoms && selectedSymptoms.length === 0 && (
            <div className="text-sm text-red-500 flex items-center gap-1 mb-2">
              <AlertCircle className="w-4 h-4" />
              Please select at least one symptom
            </div>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {commonSymptoms.map((symptom) => (
              <Button
                key={symptom}
                variant={selectedSymptoms.includes(symptom) ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setTouched({...touched, symptoms: true});
                  selectedSymptoms.includes(symptom) ? removeSymptom(symptom) : addSymptom(symptom);
                }}
                className={cn(
                  "justify-start transition-all", 
                  selectedSymptoms.includes(symptom) && "ring-2 ring-blue-200"
                )}
              >
                {symptom}
              </Button>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <div className="relative flex-grow">
              <Input
                value={customSymptom}
                onChange={(e) => setCustomSymptom(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customSymptom.trim()) {
                    addCustomSymptom();
                    setCustomSymptom("");
                    e.preventDefault();
                  }
                }}
                placeholder="Add a custom symptom"
                className="flex-grow pr-10"
                aria-label="Custom symptom input"
                id="custom-symptom-input"
                aria-describedby="custom-symptom-help"
              />
              {customSymptom.trim().length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setCustomSymptom("")}
                  type="button"
                  aria-label="Clear custom symptom input"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              className="sm:w-auto w-full flex items-center justify-center gap-1 mt-1 sm:mt-0"
              onClick={() => {
                if (customSymptom.trim()) {
                  addCustomSymptom();
                  setCustomSymptom("");
                  // Focus back on the input after adding
                  document.getElementById('custom-symptom-input')?.focus();
                }
              }}
              disabled={!customSymptom.trim()}
              type="button"
              aria-label="Add custom symptom"
            >
              <Plus className="h-4 w-4" /> Add Symptom
            </Button>
          </div>
          <p id="custom-symptom-help" className="text-xs text-gray-500 mt-1">
            Press Enter to add your symptom or use the Add Symptom button
          </p>

          {selectedSymptoms.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium mb-1">Selected symptoms:</div>
              <div className="flex flex-wrap gap-2">
                {selectedSymptoms.length > 0 ? (
                  selectedSymptoms.map((symptom) => (
                    <Badge 
                      key={symptom} 
                      variant="secondary" 
                      className="p-2 group transition-all hover:bg-gray-200"
                    >
                      {symptom}{" "}
                      <button
                        onClick={() => removeSymptom(symptom)}
                        className="ml-1 text-gray-500 group-hover:text-gray-700 transition-colors"
                        aria-label={`Remove ${symptom}`}
                        tabIndex={0} // Make it focusable for keyboard users
                        onKeyDown={(e) => {
                          // Allow removing via Enter or Space key
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            removeSymptom(symptom);
                          }
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 py-2">No symptoms selected</div>
                )}
              </div>
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Severity Selection */}
      <CollapsibleCard
        title={<div className="flex items-center gap-2">
          <span>How severe are your symptoms?</span>
          {getValidationState("severity", severity) === "valid" && (
            <CheckCircle className="w-5 h-5 text-green-500" />
          )}
          {getValidationState("severity", severity) === "invalid" && (
            <AlertCircle className="w-5 h-5 text-red-500" />
          )}
        </div>}
        defaultOpen={true}
      >
        <div className="space-y-4">
          {touched.severity && !severity && (
            <div className="text-sm text-red-500 flex items-center gap-1 mb-2">
              <AlertCircle className="w-4 h-4" />
              Please select a severity level
            </div>
          )}
          
          {severityLevels.map((level) => (
            <div
              key={level.value}
              className={`p-4 rounded-lg cursor-pointer transition-all ${severity === level.value ? level.color + ' ring-2 ring-offset-2 shadow-sm' : 'bg-gray-50 hover:bg-gray-100'}`}
              onClick={() => {
                setTouched({...touched, severity: true});
                setSeverity(level.value);
              }}
              role="radio"
              aria-checked={severity === level.value}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSeverity(level.value)}
            >
              <div className="font-medium">{level.label}</div>
            </div>
          ))}
        </div>
      </CollapsibleCard>

      {/* Duration and Onset */}
      <div className="grid md:grid-cols-2 gap-6">
        <CollapsibleCard 
          title={
            <div className="flex items-center gap-2">
              <span>When did the symptoms start?</span>
              {getValidationState("duration", duration) === "valid" && (
                <CheckCircle className="w-5 h-5 text-green-500" />
              )}
              {getValidationState("duration", duration) === "invalid" && (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
          } 
          defaultOpen={true}
        >
          {touched.duration && !duration && (
            <div className="text-sm text-red-500 flex items-center gap-1 mb-2">
              <AlertCircle className="w-4 h-4" />
              Please select when symptoms started
            </div>
          )}
          
          <Select 
            value={duration} 
            onValueChange={(val) => {
              setTouched({...touched, duration: true});
              setDuration(val);
            }}
          >
            <SelectTrigger className={cn(
              touched.duration && !duration && "border-red-500"
            )}>
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="few-hours">A few hours</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Since yesterday</SelectItem>
              <SelectItem value="few-days">A few days</SelectItem>
              <SelectItem value="week">About a week</SelectItem>
              <SelectItem value="weeks">Several weeks</SelectItem>
              <SelectItem value="months">Months</SelectItem>
            </SelectContent>
          </Select>
        </CollapsibleCard>

        <CollapsibleCard 
          title={
            <div className="flex items-center gap-2">
              <span>How did the symptoms start?</span>
              {getValidationState("onset", onset) === "valid" && (
                <CheckCircle className="w-5 h-5 text-green-500" />
              )}
              {getValidationState("onset", onset) === "invalid" && (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
          } 
          defaultOpen={true}
        >
          {touched.onset && !onset && (
            <div className="text-sm text-red-500 flex items-center gap-1 mb-2">
              <AlertCircle className="w-4 h-4" />
              Please select how symptoms started
            </div>
          )}
          
          <Select 
            value={onset} 
            onValueChange={(val) => {
              setTouched({...touched, onset: true});
              setOnset(val);
            }}
          >
            <SelectTrigger className={cn(
              touched.onset && !onset && "border-red-500"
            )}>
              <SelectValue placeholder="Select onset" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sudden">Suddenly</SelectItem>
              <SelectItem value="gradual">Gradually</SelectItem>
              <SelectItem value="after-activity">After physical activity</SelectItem>
              <SelectItem value="after-eating">After eating</SelectItem>
              <SelectItem value="during-sleep">During sleep</SelectItem>
              <SelectItem value="unknown">Don't know</SelectItem>
            </SelectContent>
          </Select>
        </CollapsibleCard>
      </div>

      {/* Vital Signs (Optional) */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            <span>Vital Signs (Optional)</span>
          </div>
        }
        description="If you have measured any of these, please include them"
        defaultOpen={false}
      >
        <div className="space-y-2 mb-2">
          <p className="text-sm text-gray-600">
            If you have recent vital signs available, you can enter them below. This information helps us better understand your condition, but it's optional.
          </p>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="temperature" className="flex items-center gap-2 text-sm font-medium">
              <Thermometer className="h-4 w-4" /> Temperature (°F)
            </Label>
            <div className="relative mt-1">
              <Input
                id="temperature"
                type="number"
                value={vitals.temperature || ""}
                onChange={(e) => {
                  // Basic validation for reasonable temperature range
                  const value = parseFloat(e.target.value);
                  if (isNaN(value) || value >= 90 && value <= 110 || e.target.value === "") {
                    setVitals({ ...vitals, temperature: e.target.value === "" ? undefined : parseFloat(e.target.value) })
                  }
                }}
                className="pr-8"
                placeholder="e.g., 98.6"
                aria-describedby="temperature-help"
                min="90"
                max="110"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                °F
              </span>
            </div>
            <p id="temperature-help" className="text-xs text-gray-500 mt-1">Normal range: 97.7-99.5°F</p>
          </div>

          <div>
            <Label htmlFor="heartRate" className="flex items-center gap-2 text-sm font-medium">
              <Heart className="h-4 w-4" /> Heart Rate (BPM)
            </Label>
            <div className="relative mt-1">
              <Input
                id="heartRate"
                type="number"
                value={vitals.heartRate || ""}
                onChange={(e) => {
                  // Basic validation for reasonable heart rate range
                  const value = parseFloat(e.target.value);
                  if (isNaN(value) || value >= 30 && value <= 220 || e.target.value === "") {
                    setVitals({ ...vitals, heartRate: e.target.value })
                  }
                }}
                className="pr-10"
                placeholder="e.g., 70"
                aria-describedby="heartrate-help"
                min="30"
                max="220"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                BPM
              </span>
            </div>
            <p id="heartrate-help" className="text-xs text-gray-500 mt-1">Normal adult range: 60-100 BPM</p>
          </div>

          <div>
            <Label htmlFor="bloodPressure" className="flex items-center gap-2 text-sm font-medium">
              <Activity className="h-4 w-4" /> Blood Pressure
            </Label>
            <div className="relative mt-1">
              <Input
                id="bloodPressure"
                value={vitals.bloodPressure || ""}
                onChange={(e) => {
                  // Allow typical blood pressure format pattern
                  if (/^$|^\d{1,3}(\/\d{1,3})?$/.test(e.target.value)) {
                    setVitals({ ...vitals, bloodPressure: e.target.value })
                  }
                }}
                className="pr-12"
                placeholder="e.g., 120/80"
                aria-describedby="bp-help"
                onBlur={(e) => {
                  // Format as XX/YY on blur if only first number entered
                  if (e.target.value && !e.target.value.includes("/")) {
                    setVitals({ ...vitals, bloodPressure: `${e.target.value}/` })
                  }
                }}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                mmHg
              </span>
            </div>
            <p id="bp-help" className="text-xs text-gray-500 mt-1">Format as systolic/diastolic (e.g. 120/80)</p>
          </div>

          <div>
            <Label htmlFor="oxygenSaturation" className="flex items-center gap-2 text-sm font-medium">
              <Activity className="h-4 w-4" /> Oxygen Saturation
            </Label>
            <div className="relative mt-1">
              <Input
                id="oxygenSaturation"
                type="number"
                value={vitals.oxygenSaturation || ""}
                onChange={(e) => {
                  // Basic validation for reasonable O2 range
                  const value = parseFloat(e.target.value);
                  if (isNaN(value) || value >= 70 && value <= 100 || e.target.value === "") {
                    setVitals({ ...vitals, oxygenSaturation: e.target.value })
                  }
                }}
                className="pr-6"
                placeholder="e.g., 98"
                aria-describedby="o2-help"
                min="70"
                max="100"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                %
              </span>
            </div>
            <p id="o2-help" className="text-xs text-gray-500 mt-1">Normal range: 95-100%</p>
          </div>
        </div>
      </CollapsibleCard>

      {/* Additional Information */}
      <CollapsibleCard title="Additional Information" defaultOpen={false}>
        <div className="space-y-2">
          <Label htmlFor="additionalInfo" className="flex justify-between">
            <span>Is there anything else you'd like us to know about your condition?</span>
            <span className="text-xs text-gray-500">(Optional)</span>
          </Label>
          <Textarea
            id="additionalInfo"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="Enter any additional details that might be relevant to your symptoms or medical history"
            rows={3}
            className="resize-y"
            aria-describedby="info-help"
          />
          <p id="info-help" className="text-sm text-gray-600">
            This could include recent exposures, recent travel, related medical history, or other context about your symptoms.
          </p>
        </div>
      </CollapsibleCard>

      {/* Form completion summary - only shown if validation fails */}
      {(touched.symptoms || touched.severity || touched.duration || touched.onset) && !isComplete && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
          <h3 className="font-medium flex items-center gap-1 text-amber-800 mb-2">
            <Info className="w-5 h-5" /> Please complete the following required information:
          </h3>
          <ul className="list-disc pl-5 text-sm space-y-1 text-amber-700">
            {!selectedSymptoms.length && <li>Select at least one symptom</li>}
            {!severity && <li>Select the severity of your symptoms</li>}
            {!duration && <li>Select when your symptoms started</li>}
            {!onset && <li>Select how your symptoms started</li>}
          </ul>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <div className="text-sm font-medium">
          {formProgress === 100 
            ? <span className="text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Form complete!
              </span> 
            : `${formProgress}% complete`}
        </div>
        <Button 
          onClick={handleSubmit} 
          disabled={!isComplete} 
          size="lg"
          className={isComplete ? "bg-blue-600 hover:bg-blue-700" : ""}
        >
          Continue to Assessment
        </Button>
      </div>
    </div>
  );
}
